import React from "react";
import { Mail, MailOpen, MousePointer2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

/**
 * Email engagement display.
 * Compact: small badge for use in lead-row tables.
 * Full: detailed card for the lead-detail drawer.
 */

const KIND_LABEL = {
    admin: "Admin notification",
    client: "Client confirmation",
    agent: "Agent assignment",
};

function fmtAgo(iso) {
    if (!iso) return null;
    try {
        const ms = Date.now() - new Date(iso).getTime();
        const min = Math.floor(ms / 60000);
        if (min < 1) return "just now";
        if (min < 60) return `${min}m ago`;
        const hr = Math.floor(min / 60);
        if (hr < 24) return `${hr}h ago`;
        const d = Math.floor(hr / 24);
        return `${d}d ago`;
    } catch {
        return null;
    }
}

export function LeadEngagementBadge({ lead }) {
    const events = lead?.email_events;
    if (!events) return null;
    const client = events.client;
    if (!client) return null;
    if (client.bounced) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200" title={client.bounce_reason || "Bounced"}>
                <AlertTriangle className="w-3 h-3" /> Bounced
            </span>
        );
    }
    if (client.clicked_count > 0) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" title="Clicked the Calendly link">
                <MousePointer2 className="w-3 h-3" /> Clicked
            </span>
        );
    }
    if (client.opened_count > 0) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" title={`Opened ${client.opened_count}x · ${fmtAgo(client.last_opened_at) || "recently"}`}>
                <MailOpen className="w-3 h-3" /> Opened {client.opened_count > 1 ? `${client.opened_count}x` : ""}
            </span>
        );
    }
    if (client.delivered_at) {
        return (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200">
                <CheckCircle2 className="w-3 h-3" /> Delivered
            </span>
        );
    }
    return null;
}

export function LeadEngagementCard({ lead }) {
    const events = lead?.email_events;
    if (!events) return null;
    const kinds = ["client", "admin", "agent"].filter((k) => events[k]);
    if (kinds.length === 0) return null;

    return (
        <div className="mt-8 border-t border-slate-200 pt-6" data-testid="email-engagement-card">
            <div className="uppercase tracking-[0.2em] text-[11px] font-semibold text-slate-700 mb-3">Email engagement</div>
            <div className="space-y-2">
                {kinds.map((kind) => (
                    <EngagementRow key={kind} kind={kind} ev={events[kind]} />
                ))}
            </div>
        </div>
    );
}

function EngagementRow({ kind, ev }) {
    const stages = [];
    if (ev.sent_at) stages.push({ icon: Mail, label: "Sent", at: ev.sent_at, tone: "text-slate-700" });
    if (ev.delivered_at) stages.push({ icon: CheckCircle2, label: "Delivered", at: ev.delivered_at, tone: "text-sky-600" });
    if (ev.opened_count > 0) stages.push({ icon: MailOpen, label: `Opened${ev.opened_count > 1 ? ` ${ev.opened_count}×` : ""}`, at: ev.last_opened_at, tone: "text-amber-700" });
    if (ev.clicked_count > 0) stages.push({ icon: MousePointer2, label: `Clicked${ev.clicked_count > 1 ? ` ${ev.clicked_count}×` : ""}`, at: ev.last_clicked_at, tone: "text-emerald-700" });
    if (ev.bounced) stages.push({ icon: AlertTriangle, label: "Bounced", at: ev.bounced_at, tone: "text-rose-700" });
    if (ev.complained) stages.push({ icon: AlertTriangle, label: "Spam reported", at: ev.complained_at, tone: "text-rose-700" });

    const last = stages[stages.length - 1];

    return (
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={`engagement-${kind}`}>
            <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-semibold text-slate-700 truncate">
                    {KIND_LABEL[kind]}
                    <span className="text-slate-600 font-normal ml-1.5 text-[11px]">→ {ev.to || ""}</span>
                </div>
                {last?.at && (
                    <div className="flex items-center gap-1 text-[11px] text-slate-700 shrink-0">
                        <Clock className="w-3 h-3" /> {fmtAgo(last.at)}
                    </div>
                )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
                {stages.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <span key={i} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border border-slate-200 ${s.tone}`}>
                            <Icon className="w-3 h-3" /> {s.label}
                        </span>
                    );
                })}
                {stages.length === 0 && <span className="text-[11px] text-slate-600">Awaiting delivery confirmation…</span>}
            </div>
        </div>
    );
}
